import { z } from "zod";
import { paginationSchema } from "./common.js";

export const crewMemberInputSchema = z.object({
  workerId: z.string().uuid(),
  defaultJobCode: z.string().trim().max(64).optional().nullable(),
  defaultCostCode: z.string().trim().max(64).optional().nullable(),
  defaultEquipmentId: z.string().uuid().optional().nullable(),
  role: z.string().trim().max(100).optional().nullable(),
});

export const createCrewSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  description: z.string().trim().max(2000).optional().nullable(),
  siteId: z.string().uuid().optional().nullable(),
  active: z.boolean().default(true),
  members: z.array(crewMemberInputSchema).default([]),
});

export const updateCrewSchema = createCrewSchema.partial();

export const crewListQuerySchema = paginationSchema.extend({
  siteId: z.string().uuid().optional(),
  active: z.coerce.boolean().optional(),
  q: z.string().trim().optional(),
});

export const crewMemberSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  employeeId: z.string(),
  workerName: z.string(),
  workerStatus: z.enum(["ACTIVE", "INACTIVE"]),
  trade: z.string().nullable(),
  defaultJobCode: z.string().nullable(),
  defaultCostCode: z.string().nullable(),
  defaultEquipmentId: z.string().uuid().nullable(),
  defaultEquipmentName: z.string().nullable(),
  role: z.string().nullable(),
});

export const crewSummarySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  siteId: z.string().uuid().nullable(),
  siteName: z.string().nullable(),
  siteCode: z.string().nullable(),
  active: z.boolean(),
  memberCount: z.number().int(),
  inactiveMemberCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const crewDetailSchema = crewSummarySchema.extend({
  canEdit: z.boolean(),
  members: z.array(crewMemberSchema),
});

export const crewFormOptionsSchema = z.object({
  sites: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
  workers: z.array(
    z.object({
      id: z.string().uuid(),
      employeeId: z.string(),
      displayName: z.string(),
      status: z.enum(["ACTIVE", "INACTIVE"]),
      siteId: z.string().uuid().nullable(),
      trade: z.string().nullable(),
    }),
  ),
  equipment: z.array(
    z.object({
      id: z.string().uuid(),
      equipmentId: z.string(),
      name: z.string(),
      siteId: z.string().uuid().nullable(),
    }),
  ),
});

export type CrewMemberInput = z.infer<typeof crewMemberInputSchema>;
export type CreateCrewInput = z.infer<typeof createCrewSchema>;
export type UpdateCrewInput = z.infer<typeof updateCrewSchema>;
export type CrewSummary = z.infer<typeof crewSummarySchema>;
export type CrewDetail = z.infer<typeof crewDetailSchema>;
export type CrewFormOptions = z.infer<typeof crewFormOptionsSchema>;
